<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>

  <div id="page-risk-codes-list" class="ag-grid-container">

    <div class="vx-card">
      <grid-panel
          height="xl"
          :column-defs="columnDefs"
          :url="() => this.$api.clientMap.list()"
          :auto-size="true"
          title="Client Maps"
      >
        <template v-slot:top-right>
            <vs-button
                v-if="rowsToUpdate.length && componentAccess.canEdit.access"
                color="light"
                class="mx-4"
                @click="save"
            >
              Save
            </vs-button>
        </template>

        <vs-dropdown-item v-if="componentAccess.canCreate.access">
          <span
              class="flex items-center"
              @click="$router.push({ name: 'client-map.create' })"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Create Client Map</span>
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item v-if="componentAccess.canView.access">
          <span
              class="flex items-center"
              @click="openModal('bulkUpdateProducer')"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Bulk update producer commissions</span>
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item v-if="componentAccess.canView.access">
          <span
              class="flex items-center"
              @click="openModal('bulkUpdateNewProduct')"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Bulk update new product commissions</span>
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item v-if="componentAccess.canView.access">
          <span
              class="flex items-center"
              @click="openModal('uploadProducerCommission')"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Upload Producer Commission</span>
          </span>
        </vs-dropdown-item>
      </grid-panel>

    </div>

    <bulk-update-producer-commissions-modal
        :visible="modals.bulkUpdateProducer.visible"
        @close="closeModal('bulkUpdateProducer')"
        @cancel="closeModal('bulkUpdateProducer')"
    />
    <bulk-update-new-product-commissions-modal
        :visible="modals.bulkUpdateNewProduct.visible"
        @close="closeModal('bulkUpdateNewProduct')"
        @cancel="closeModal('bulkUpdateNewProduct')"
    />
    <upload-modal
        :visible="modals.uploadProducerCommission.visible"
        @close="closeModal('uploadProducerCommission')"
        @cancel="closeModal('uploadProducerCommission')"
    />
  </div>

</template>

<script>

import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'

// Cell Renderer
import GridPanel from "@/views/components/ag-grid/GridPanel";
import CellRendererButton from "@/views/components/ag-grid/cell-renderer/CellRendererButton";
import NotificationMixin from "@/components/mixins/NotificationMixin";
import CellRendererCheckbox from "@/views/components/ag-grid/cell-renderer/CellRendererCheckbox";
import {clone} from "lodash";
import CanAccessMixin from "@/components/mixins/CanAccessMixin";
import BulkUpdateProducerCommissionsModal from "@/views/pages/client-map/BulkUpdateProducerCommissionsModal";
import BulkUpdateNewProductCommissionsModal from "@/views/pages/client-map/BulkUpdateNewProductCommissionsModal";
import UploadModal from "@/views/pages/client-map/ClientMapUploadModal";

export default {
  components: {
    GridPanel,
    CellRendererButton,
    BulkUpdateProducerCommissionsModal,
    BulkUpdateNewProductCommissionsModal,
    UploadModal,
    CellRendererCheckbox,
  },
  mixins: [NotificationMixin, CanAccessMixin],
  data () {
    return {
      searchQuery: '',
      componentAccess: {
        canEdit: {
          route: 'client-map.edit',
          access: false,
        },
        canCreate: {
          route: 'client-map.create',
          access: false,
        },
        canView: {
          route: 'client-map.view',
          access: false,
        },
      },
      rowsToUpdate: [],
      countries: [],
      connectionTypes: [],

      modals: {
        bulkUpdateProducer: {
          visible: false,
        },
        bulkUpdateNewProduct: {
          visible: false,
        },
        uploadProducerCommission: {
          visible: false,
        }
      },

      // AgGrid
      columnDefs: [
        {
          headerName: 'id',
          field: 'id',
          minWidth: 220,
          hide: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
        },
        {
          headerName: 'Producer Code',
          field: 'CompanyCode',
          minWidth: 120,
          filter: 'agTextColumnFilter',
          sortable: true,
          sort: 'asc',
        },
        {
          headerName: 'Company',
          field: 'CompanyName',
          minWidth: 90,
          filter: 'agTextColumnFilter',
          sortable: true,
        },
        {
          headerName: 'Country',
          field: 'SalesCountryType_FK',
          filter: 'agTextColumnFilter',
          filterValueGetter: ({ data }) => (data && data.SalesCountryName) || '',
          editable: true,
          minWidth: 100,
          cellEditor: 'agRichSelectCellEditor',
          cellEditorPopup: true,
          cellEditorParams: () => ({
            values: [null, ...this.countries.map((c) => c.id)],
            formatValue: (value) => {
              if (!value) return '';
              const item = this.countries.find((c) => c.id === value);
              return item ? item.label : '';
            },
          }),
          valueFormatter: ({ data }) => (data && data.SalesCountryName) || '',
          valueSetter: ({ data, newValue }) => {
            data.SalesCountryType_FK = newValue;
            const item = this.countries.find((c) => c.id === newValue);
            data.SalesCountryName = item ? item.label : null;
            this.rowsToUpdate.push(data);
            return true;
          },
        },
        {
          headerName: 'Product',
          field: 'ProductCode',
          filter: 'agTextColumnFilter',
          minWidth: 125,
        },
        {
          headerName: 'Connection Type',
          field: 'OnlineRaterConnectionType_FK',
          filter: 'agTextColumnFilter',
          filterValueGetter: ({ data }) => (data && data.OnlineRaterConnectionTypeName) || '',
          editable: true,
          minWidth: 130,
          cellEditor: 'agRichSelectCellEditor',
          cellEditorPopup: true,
          cellEditorParams: () => ({
            values: [null, ...this.connectionTypes.map((c) => c.id)],
            formatValue: (value) => {
              if (!value) return '';
              const item = this.connectionTypes.find((c) => c.id === value);
              return item ? item.label : '';
            },
          }),
          valueFormatter: ({ data }) => (data && data.OnlineRaterConnectionTypeName) || '',
          valueSetter: ({ data, newValue }) => {
            data.OnlineRaterConnectionType_FK = newValue;
            const item = this.connectionTypes.find((c) => c.id === newValue);
            data.OnlineRaterConnectionTypeName = item ? item.label : null;
            this.rowsToUpdate.push(data);
            return true;
          },
        },
        {
          headerName: 'Remote Signature %',
          field: 'RemoteSignature_PC',
          filter: 'agTextColumnFilter',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
            data.RemoteSignature_PC === ".00" ? 'Null' : data.RemoteSignature_PC,
        },
        {
          headerName: 'Manual Signature %',
          field: 'ManualSignature_PC',
          filter: 'agTextColumnFilter',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
            data.ManualSignature_PC === ".00" ? 'Null' : data.ManualSignature_PC,
        },
        {
          headerName: 'Affinity Remote Signature %',
          field: 'AffinityRemoteSignature_PC',
          filter: 'agTextColumnFilter',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
            data.AffinityRemoteSignature_PC === ".00" ? 'Null' : data.AffinityRemoteSignature_PC,
        },
        {
          headerName: 'Affinity Manual Signature %',
          field: 'AffinityManualSignature_PC',
          filter: 'agTextColumnFilter',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
            data.AffinityManualSignature_PC === ".00" ? 'Null' : data.AffinityManualSignature_PC,
        },
        {
          headerName: 'Consultancy Fee %',
          field: 'ConsultancyFee_PC',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
              data.ConsultancyFee_PC === ".00" ? 'Null' : data.ConsultancyFee_PC,
        },
        {
          headerName: 'DLDD %',
          field: 'DiscretionaryLoading_PC',
          editable: true,
          minWidth: 75,
          valueSetter: this.validateFloat,
          valueGetter: ({ data }) =>
              data.DiscretionaryLoading_PC === ".00" ? 'Null' : data.DiscretionaryLoading_PC,
        },
        {
          headerName: 'Is Consultancy Fee Allowed',
          cellRenderer: "CellRendererCheckbox",
          valueSetter: ({colDef, data, newValue}) => {
            data[colDef.field] = newValue;

            this.rowsToUpdate.push(data);
          },
          field: 'IsConsultancyFeeAllowed',
          minWidth: 75,
        },
      ],
    }
  },
  watch: {
    rowsToUpdate(value) {
      console.log(value);
    }
  },
  async created() {
    try {
      const [countriesRes, connectionTypesRes] = await Promise.all([
        this.$api.clientMap.listSalesCountries(),
        this.$api.clientMap.listConnectionTypes(),
      ]);
      this.countries = (countriesRes.data && countriesRes.data.data) || countriesRes.data || [];
      this.connectionTypes = (connectionTypesRes.data && connectionTypesRes.data.data) || connectionTypesRes.data || [];
    } catch (e) {
      console.error('Failed to load dropdown options', e);
    }
  },
  methods: {
    openModal(modalName) {
      this.modals[modalName].visible = true;
    },
    closeModal(modalName) {
      this.modals[modalName].visible = false;
    },
    async save() {
      if (!this.rowsToUpdate.length) {
        this.notifyWarning('Nothing to update');
      }

      try {
        await this.$api.clientMap.updateMany(this.rowsToUpdate);
        this.notifySuccess('Client Map updated');

        this.rowsToUpdate = [];
      } catch(e) {
        this.notifyError('Client Map failed to update');
        console.log(e);
      }
    },
    validateFloat({colDef, data, newValue}) {
      let value = parseFloat(newValue);

      if (isNaN(value)) {
        this.notifyError('Value must be a number.');
        return;
      }

      if (value < 0) {
        this.notifyError('Number must be above 0.');
        return;
      }

      data[colDef.field] = value.toFixed(2);

      this.rowsToUpdate.push(data);
    }
  },
}

</script>

<style lang="scss">
</style>
